import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.date}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-3">
            {article.category}
          </span>
          <h3 className="text-lg font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
            {article.category}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2">{article.excerpt}</p>
      </div>
    </Link>
  );
}